import { useState } from "react";
import { postRequest, showMessage } from '../../../../config';
import ModuleForm from "../ModuleForm";
import AIGenerationProgress from "../ai/AIGenerationProgress";

/**
 * ModuleCreate
 *
 * Handles two flows transparently:
 *
 *   Manual (generationMode === 'manual' or undefined):
 *     POST /modules/temp-modules  (with sections)  ← existing flow, unchanged
 *
 *   AI (generationMode === 'ai'):
 *     1. POST /modules/temp-modules  (empty sections) → get moduleId
 *     2. POST /modules/generate-ai-module              → get jobId
 *     3. Render <AIGenerationProgress> with socket + polling
 */

const ModuleCreate = ({ onSuccess, onCancel }) => {
    const [isSubmitting, setIsSubmitting]   = useState(false);

    // AI progress state
    const [aiJob,       setAiJob]           = useState(null);   // { jobId, moduleId, moduleName, estimatedSections, estimatedQuestions }
    const [showProgress,setShowProgress]    = useState(false);

    // ── Manual submit (unchanged) ───────────────────────────────────────────
    const handleManualSubmit = async (formData) => {
        const response = await postRequest('/modules/temp-modules', formData);
        if (response?.code === 200) {
            const result = response.body || response;
            showMessage(response.message || 'Module created successfully!');
            console.log(`Created: ${result.sectionsCreated || 0} sections, ${result.questionsCreated || 0} questions`);
            if (onSuccess) onSuccess(result);
            if (onCancel)  onCancel();
        } else {
            showMessage(response.message || 'Failed to create module');
        }
    };

    // ── AI submit ───────────────────────────────────────────────────────────
    const handleAISubmit = async (formData) => {
        const { generationMode, aiConfig, ...modulePayload } = formData;

        // Step 1 — Create the module skeleton (no sections)
        const createRes = await postRequest('/modules/temp-modules', {
            ...modulePayload,
            sections: [],
        });

        if (createRes?.code !== 200) {
            showMessage(createRes?.message || 'Failed to create module');
            return;
        }

        const moduleId   = createRes.body?.module?._id || createRes.body?._id;
        const moduleName = createRes.body?.module?.moduleName || formData.moduleName;

        if (!moduleId) {
            showMessage('Module created but ID not returned. Please try manual mode.');
            return;
        }

        // Step 2 — Trigger AI generation
        const aiRes = await postRequest('/modules/generate-ai-module', {
            moduleId,
            generationMode: 'ai',
            sectionsPerCombo:    aiConfig?.sectionsPerCombo    ?? 5,
            questionsPerSection: aiConfig?.questionsPerSection ?? 10,
        });

        if (aiRes?.code !== 200) {
            showMessage(aiRes?.message || 'Failed to start AI generation. You can trigger it from the module editor.');
            // Still switch to progress view — module was created
        }

        const jobData = aiRes?.body || {};

        // Step 3 — Show progress UI
        setAiJob({
            jobId:              jobData.jobId,
            moduleId,
            moduleName,
            moduleImageUrl:     modulePayload.imageURL || '',
            estimatedSections:  jobData.estimatedSections  || 0,
            estimatedQuestions: jobData.estimatedQuestions || 0,
        });
        setShowProgress(true);

        showMessage('Module created! AI generation started in the background.');
    };

    // ── Unified submit handler ──────────────────────────────────────────────
    const handleSubmit = async (formData) => {
        setIsSubmitting(true);
        try {
            if (formData.generationMode === 'ai') {
                await handleAISubmit(formData);
            } else {
                await handleManualSubmit(formData);
            }
        } catch (error) {
            console.error('Error creating module:', error);
            showMessage('Failed to create module. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancel = () => {
        if (onCancel) onCancel();
        else window.history.back();
    };

    // ── AI progress view ────────────────────────────────────────────────────
    if (showProgress && aiJob) {
        return (
            <div className="modules-container" style={{ padding: '2rem' }}>
                <AIGenerationProgress
                    jobId={aiJob.jobId}
                    moduleId={aiJob.moduleId}
                    moduleName={aiJob.moduleName}
                    moduleImageUrl={aiJob.moduleImageUrl}
                    estimatedSections={aiJob.estimatedSections}
                    estimatedQuestions={aiJob.estimatedQuestions}
                    onComplete={(job) => {
                        // Fired once on terminal status — do NOT navigate away automatically;
                        // let the admin click "View / Edit Module" deliberately.
                        console.log('AI generation finished:', job?.status);
                    }}
                    onViewModule={() => {
                        if (onSuccess) onSuccess({ module: { _id: aiJob.moduleId } });
                        if (onCancel)  onCancel();
                    }}
                />
            </div>
        );
    }

    // ── Form view ───────────────────────────────────────────────────────────
    return (
        <div className="modules-container" style={{ padding: '2rem' }}>
            <ModuleForm
                moduleData={null}
                onFormChange={() => {}}
                onSubmit={handleSubmit}
                onCancel={handleCancel}
                isEdit={false}
                isSubmitting={isSubmitting}
            />
        </div>
    );
};

export default ModuleCreate;
